import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import StudyActivitiesTable from '@/tables/StudyActivitiesTable';
import { useStudyActivitiesTabAccess } from '~/hooks/useStudyActivitiesTabAccess';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

function StudyActivitiesScreen() {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  // One table for every actor and every group type: legacy renders a single
  // `HtmlTableRenderer('study-group-study-activities')`
  // (`buddypress/groups/single/students/study-activities.php:8-11`), and a group
  // coordinator was measured live getting the identical filter set, table name
  // and columns. The variant this route used to branch into had no filters and a
  // `step` union missing Lesson and Access.
  const { canView, isResolved } = useStudyActivitiesTabAccess();
  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });

  // Not a no-op: painting the heading before the group type lands would flash it
  // at a viewer the next line refuses.
  if (!isResolved) return null;
  // Refuse unless both the group's type and the viewer's role say yes. The
  // Students layout guard admits five group types and any platform admin, where
  // this tab exists on three, so a refused caller reaches this leaf with the tab
  // bar already rendered — and without a gate here the endpoint's 403 paints an
  // empty table (the reference table renders `data ?? []`), which reads as "this
  // group has no study activity" rather than as a denial.
  if (!canView) return <NotFoundScreen />;

  return (
    <>
      <GroupHeaderWrapper>
        {/* `helpKey` is the legacy section id, not the tab title: `HelpModal`
            matches lowercased ENGLISH ids, so a translated title selects the
            first help item for every non-English user. This template anchors
            `wbs_contextual_helper('students')` (`study-activities.php:3`), as do
            all four of its siblings under `groups/single/students/` — the help
            id is a section, not a per-table name. */}
        <TitleWithQuestion
          title={t('lessons.study-activities')}
          helpKey="students"
        />
      </GroupHeaderWrapper>
      <StudyActivitiesTable groupId={groupData?.id ?? 0} />
    </>
  );
}

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/students/study-activities',
)({
  component: StudyActivitiesScreen,
});
